import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import storageApi from '../../utils/storageApi';
import ConfirmationModal from '../ConfirmationModal';
import AlertModal from '../AlertModal';
import { DuplicateEmailsPage } from '../duplicates';
import './storage.css';

const MODULES = [
  { id: 'overview', label: 'Overview' },
  { id: 'duplicates', label: 'Duplicate Emails' },
  { id: 'attachments', label: 'Large Attachments' },
  { id: 'dup-attachments', label: 'Duplicate Attachments' },
  { id: 'old', label: 'Old Emails' },
  { id: 'newsletters', label: 'Newsletters' },
  { id: 'trash', label: 'Empty Trash' },
  { id: 'spam', label: 'Empty Spam' },
  { id: 'labels', label: 'Unused Labels' },
  { id: 'folders', label: 'Empty Folders' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'rules', label: 'Auto Cleanup' },
  { id: 'settings', label: 'Settings' },
];

const FOLDER_COLORS = {
  inbox: '#1a73e8',
  sent: '#34a853',
  drafts: '#fbbc04',
  trash: '#ea4335',
  spam: '#fa7b17',
  archive: '#9334e6',
};

const formatBytes = (n = 0) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return '—';
  }
};

export default function StorageManagerPage({ currentUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const module = searchParams.get('module') || 'overview';

  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);

  const [attachments, setAttachments] = useState({ items: [], total: 0 });
  const [minMb, setMinMb] = useState(5);
  const [dupAtt, setDupAtt] = useState(null);
  const [oldMails, setOldMails] = useState({ items: [], total: 0 });
  const [olderThan, setOlderThan] = useState('1y');
  const [newsletters, setNewsletters] = useState({ items: [], total: 0 });
  const [trashInfo, setTrashInfo] = useState(null);
  const [spamInfo, setSpamInfo] = useState(null);
  const [unusedLabels, setUnusedLabels] = useState([]);
  const [emptyFolders, setEmptyFolders] = useState([]);
  const [query, setQuery] = useState('');
  const [folder, setFolder] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [moduleLoading, setModuleLoading] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '' });
  const showAlert = (message) => setAlert({ open: true, message });

  const setModule = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'overview') next.delete('module');
    else next.set('module', id);
    setSearchParams(next, { replace: true });
  };

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, rp] = await Promise.all([storageApi.overview(), storageApi.reports()]);
      if (ov.success) setOverview(ov.data);
      if (rp.success) setReports(rp.data);
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Failed to load storage overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handleScan = async (includeDuplicates = true) => {
    setScanning(true);
    try {
      const res = await storageApi.scan({ includeDuplicates });
      if (res.success) {
        setOverview(res.data?.overview || res.data);
        showAlert(res.message || 'Storage scan complete');
        await loadOverview();
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const loadModuleData = useCallback(async () => {
    if (module === 'overview' || module === 'duplicates') return;
    setModuleLoading(true);
    setSelected(new Set());
    try {
      if (module === 'attachments') {
        const res = await storageApi.attachments({ minMb, q: query, page: 1, limit: 40 });
        if (res.success) setAttachments(res);
      } else if (module === 'dup-attachments') {
        const res = await storageApi.duplicateAttachments();
        if (res.success) setDupAtt(res.data);
      } else if (module === 'old') {
        const res = await storageApi.oldMails({
          olderThan,
          q: query,
          folder,
          page: 1,
          limit: 40,
        });
        if (res.success) setOldMails(res);
      } else if (module === 'newsletters') {
        const res = await storageApi.newsletters({ q: query, page: 1, limit: 40 });
        if (res.success) setNewsletters(res);
      } else if (module === 'trash') {
        const res = await storageApi.trash();
        if (res.success) setTrashInfo(res.data);
      } else if (module === 'spam') {
        const res = await storageApi.spam();
        if (res.success) setSpamInfo(res.data);
      } else if (module === 'labels') {
        const res = await storageApi.unusedLabels();
        if (res.success) setUnusedLabels(res.data?.items || []);
      } else if (module === 'folders') {
        const res = await storageApi.emptyFolders();
        if (res.success) setEmptyFolders(res.data?.items || []);
      } else if (module === 'analytics') {
        const res = await storageApi.reports();
        if (res.success) setReports(res.data);
      } else if (module === 'settings') {
        const res = await storageApi.getSettings();
        if (res.success) setSettings(res.data);
      } else if (module === 'rules') {
        const res = await storageApi.listRules();
        if (res.success) setRules(res.data || []);
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message || 'Failed to load module');
    } finally {
      setModuleLoading(false);
    }
  }, [module, minMb, olderThan, query, folder]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);

  const runCleanup = (action, mailIds = [], strategy) => {
    setConfirm({
      open: true,
      title: 'Confirm cleanup',
      message:
        'This will move items to Trash first (or apply a soft cleanup). Continue?',
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await storageApi.cleanup({ action, mailIds, strategy });
          showAlert(res.message || 'Cleanup complete');
          setSelected(new Set());
          await loadOverview();
          await loadModuleData();
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      },
    });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const folderBars = useMemo(() => {
    const folders = overview?.folders || [];
    const max = Math.max(1, ...folders.map((f) => f.size || 0));
    return folders.map((f) => ({
      ...f,
      pct: Math.round(((f.size || 0) / max) * 100),
      color: FOLDER_COLORS[f.folder] || '#64748b',
    }));
  }, [overview]);

  const pieStyle = useMemo(() => {
    const folders = overview?.folders || [];
    const total = folders.reduce((s, f) => s + (f.size || 0), 0) || 1;
    let acc = 0;
    const stops = folders.map((f) => {
      const start = (acc / total) * 360;
      acc += f.size || 0;
      const end = (acc / total) * 360;
      const color = FOLDER_COLORS[f.folder] || '#64748b';
      return `${color} ${start}deg ${end}deg`;
    });
    return {
      background: stops.length
        ? `conic-gradient(${stops.join(', ')})`
        : 'conic-gradient(#e5e7eb 0deg 360deg)',
    };
  }, [overview]);

  const saveSettings = async () => {
    try {
      const res = await storageApi.updateSettings(settings);
      if (res.success) {
        setSettings(res.data);
        showAlert('Settings saved');
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message);
    }
  };

  const addRule = async () => {
    try {
      const res = await storageApi.saveRule({
        name: 'Delete spam older than 30 days',
        type: 'spam_older_days',
        enabled: true,
        notifyBefore: true,
        params: { days: 30 },
      });
      if (res.success) {
        setRules((r) => [res.data, ...r]);
        showAlert('Rule created');
      }
    } catch (e) {
      showAlert(e.response?.data?.message || e.message);
    }
  };

  const deleteRule = (id) => {
    setConfirm({
      open: true,
      title: 'Delete rule',
      message: 'Remove this auto-cleanup rule?',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await storageApi.deleteRule(id);
          setRules((r) => r.filter((x) => x._id !== id));
        } catch (e) {
          showAlert(e.response?.data?.message || e.message);
        }
      },
    });
  };

  const renderMailTable = (items, { showAttach = false } = {}) => {
    if (!items?.length) {
      return (
        <div className="sm-empty">
          <h3>Nothing here yet</h3>
          <p>Run a storage scan or adjust filters.</p>
        </div>
      );
    }
    return (
      <table className="sm-table">
        <thead>
          <tr>
            <th className="sm-check-col" />
            <th>From</th>
            <th>Subject</th>
            <th>Folder</th>
            <th>Date</th>
            {showAttach && <th>Attachment</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const id = String(m.mailId || m.uid || `${m.from}-${m.subject}`);
            return (
              <tr key={id}>
                <td>
                  {m.mailId && (
                    <input
                      type="checkbox"
                      checked={selected.has(String(m.mailId))}
                      onChange={() => toggleSelect(String(m.mailId))}
                    />
                  )}
                </td>
                <td className="meta">{m.from || '—'}</td>
                <td>
                  <div className="subject">{m.subject || '(no subject)'}</div>
                </td>
                <td className="meta">{m.folder || '—'}</td>
                <td className="meta">{formatDate(m.date || m.createdAt)}</td>
                {showAttach && (
                  <td className="meta">
                    {m.filename || '—'}
                    <br />
                    {formatBytes(m.size)}
                  </td>
                )}
                <td>
                  <div className="sm-row-actions">
                    {m.mailId && (
                      <button
                        type="button"
                        className="sm-btn sm-btn-sm"
                        onClick={() => runCleanup('move_to_trash', [m.mailId])}
                      >
                        Trash
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="sm-page">
      <header className="sm-header">
        <div className="sm-title-wrap">
          <div className="sm-title-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
            </svg>
          </div>
          <div>
            <h1>Storage Manager</h1>
            <p>
              Analyze, clean, and optimize your mailbox — duplicates, large files, old mail,
              newsletters, and more in one place.
            </p>
          </div>
        </div>
        <div className="sm-header-actions">
          <button type="button" className="sm-btn" onClick={() => setModule('settings')}>
            Settings
          </button>
          <button
            type="button"
            className="sm-btn sm-btn-primary"
            onClick={() => handleScan(true)}
            disabled={scanning}
          >
            {scanning ? 'Scanning…' : 'Scan mailbox'}
          </button>
        </div>
      </header>

      {scanning && (
        <div className="sm-progress-scan">
          <div className="bar"><i /></div>
          <span>Analyzing storage…</span>
        </div>
      )}

      {loading ? (
        <div>
          {[1, 2, 3].map((n) => (
            <div key={n} className="sm-skeleton" />
          ))}
        </div>
      ) : (
        <>
          <section className="sm-kpi-grid">
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Total storage used</span>
              <span className="sm-kpi-value">{formatBytes(overview?.totalSize)}</span>
              <span className="sm-kpi-sub">{overview?.totalCount || 0} messages</span>
              <div className="sm-progress-wrap">
                <div className={`sm-progress ${(overview?.storagePercent || 0) >= 80 ? 'warn' : ''}`}>
                  <span style={{ width: `${Math.min(100, overview?.storagePercent || 0)}%` }} />
                </div>
                <span className="sm-kpi-sub">{overview?.storagePercent || 0}% of quota</span>
              </div>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Inbox</span>
              <span className="sm-kpi-value">{formatBytes(overview?.inboxSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Sent</span>
              <span className="sm-kpi-value">{formatBytes(overview?.sentSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Drafts</span>
              <span className="sm-kpi-value">{formatBytes(overview?.draftSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Trash</span>
              <span className="sm-kpi-value">{formatBytes(overview?.trashSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Spam</span>
              <span className="sm-kpi-value">{formatBytes(overview?.spamSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Archive</span>
              <span className="sm-kpi-value">{formatBytes(overview?.archiveSize)}</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Attachments</span>
              <span className="sm-kpi-value">{formatBytes(overview?.attachmentSize)}</span>
              <span className="sm-kpi-sub">{overview?.attachmentCount || 0} files</span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Largest folder</span>
              <span className="sm-kpi-value" style={{ textTransform: 'capitalize' }}>
                {overview?.largestFolder || '—'}
              </span>
            </div>
            <div className="sm-kpi-card">
              <span className="sm-kpi-label">Recoverable (est.)</span>
              <span className="sm-kpi-value">{formatBytes(overview?.recoverableEstimate)}</span>
              <span className="sm-kpi-sub">
                {overview?.duplicateGroups || 0} duplicate groups
              </span>
            </div>
          </section>

          <nav className="sm-modules" aria-label="Storage modules">
            {MODULES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`sm-module-chip${module === m.id ? ' active' : ''}`}
                onClick={() => setModule(m.id)}
              >
                {m.label}
              </button>
            ))}
          </nav>

          {module === 'overview' && (
            <>
              <section className="sm-panel">
                <h2>One-click cleanup</h2>
                <p className="sm-panel-desc">
                  Quick actions move mail to Trash first. Confirm before each cleanup.
                </p>
                <div className="sm-quick-grid">
                  <button type="button" className="sm-quick-btn" onClick={() => setModule('duplicates')}>
                    <strong>Clean duplicate emails</strong>
                    <span>Keep newest / oldest / starred</span>
                  </button>
                  <button
                    type="button"
                    className="sm-quick-btn"
                    onClick={() => runCleanup('empty_spam_mongo')}
                  >
                    <strong>Delete old spam</strong>
                    <span>Move spam to Trash</span>
                  </button>
                  <button
                    type="button"
                    className="sm-quick-btn"
                    onClick={() => runCleanup('empty_trash_mongo')}
                  >
                    <strong>Empty trash (app)</strong>
                    <span>Permanently remove trash in app DB</span>
                  </button>
                  <button type="button" className="sm-quick-btn" onClick={() => setModule('old')}>
                    <strong>Archive old emails</strong>
                    <span>Review mail older than 1–5 years</span>
                  </button>
                  <button type="button" className="sm-quick-btn" onClick={() => setModule('attachments')}>
                    <strong>Large attachments</strong>
                    <span>Find files over 5–25 MB</span>
                  </button>
                  <button type="button" className="sm-quick-btn" onClick={() => setModule('newsletters')}>
                    <strong>Clean newsletters</strong>
                    <span>Bulk trash promo digests</span>
                  </button>
                  <button
                    type="button"
                    className="sm-quick-btn"
                    onClick={() => handleScan(true)}
                  >
                    <strong>Optimize mailbox</strong>
                    <span>Full scan + duplicate detection</span>
                  </button>
                </div>
              </section>

              <div className="sm-charts">
                <section className="sm-panel">
                  <h2>Storage usage</h2>
                  <p className="sm-panel-desc">Folder size breakdown from last scan</p>
                  <div className="sm-bar-list">
                    {folderBars.length === 0 ? (
                      <div className="sm-empty"><p>Scan to populate folder sizes.</p></div>
                    ) : (
                      folderBars.map((f) => (
                        <div className="sm-bar-row" key={f.folder}>
                          <span className="label">{f.folder}</span>
                          <div className="sm-bar-track">
                            <i style={{ width: `${f.pct}%`, background: f.color }} />
                          </div>
                          <span className="val">{formatBytes(f.size)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </section>
                <section className="sm-panel">
                  <h2>Distribution</h2>
                  <div className="sm-pie" style={pieStyle}>
                    <div className="sm-pie-center">
                      <strong>{overview?.storagePercent || 0}%</strong>
                      used
                    </div>
                  </div>
                  <div className="sm-legend">
                    {(overview?.folders || []).map((f) => (
                      <span key={f.folder}>
                        <i style={{ background: FOLDER_COLORS[f.folder] || '#64748b' }} />
                        {f.folder}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            </>
          )}

          {module === 'duplicates' && (
            <div className="sm-panel sm-embedded-dup">
              <DuplicateEmailsPage currentUser={currentUser} embedded />
            </div>
          )}

          {module === 'attachments' && (
            <section className="sm-panel">
              <h2>Large attachments</h2>
              <p className="sm-panel-desc">Find messages with attachments above a size threshold.</p>
              <div className="sm-toolbar">
                <select
                  className="sm-select"
                  value={minMb}
                  onChange={(e) => setMinMb(Number(e.target.value))}
                >
                  <option value={5}>&gt; 5 MB</option>
                  <option value={10}>&gt; 10 MB</option>
                  <option value={25}>&gt; 25 MB</option>
                </select>
                <input
                  className="sm-input"
                  placeholder="Search sender, subject, filename…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button type="button" className="sm-btn" onClick={loadModuleData}>
                  Apply
                </button>
                {selectedIds.length > 0 && (
                  <button
                    type="button"
                    className="sm-btn sm-btn-danger"
                    onClick={() => runCleanup('move_to_trash', selectedIds)}
                  >
                    Trash selected ({selectedIds.length})
                  </button>
                )}
              </div>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : (
                renderMailTable(attachments.items, { showAttach: true })
              )}
            </section>
          )}

          {module === 'dup-attachments' && (
            <section className="sm-panel">
              <h2>Duplicate attachments</h2>
              <p className="sm-panel-desc">
                Identical files detected by SHA-256. Recoverable:{' '}
                <strong>{formatBytes(dupAtt?.recoverable)}</strong>
              </p>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : !dupAtt?.groups?.length ? (
                <div className="sm-empty">
                  <h3>No duplicate files found</h3>
                  <p>Hashes are computed for attachments stored in the app database.</p>
                </div>
              ) : (
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Copies</th>
                      <th>Size</th>
                      <th>Recoverable</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {dupAtt.groups.map((g) => (
                      <tr key={g.hash}>
                        <td className="subject">{g.filename}</td>
                        <td>{g.count}</td>
                        <td>{formatBytes(g.size)}</td>
                        <td>{formatBytes(g.recoverable)}</td>
                        <td>
                          <button
                            type="button"
                            className="sm-btn sm-btn-sm"
                            onClick={() =>
                              runCleanup(
                                'move_to_trash',
                                (g.items || []).slice(1).map((i) => i.mailId).filter(Boolean)
                              )
                            }
                          >
                            Trash extras
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {module === 'old' && (
            <section className="sm-panel">
              <h2>Old emails</h2>
              <p className="sm-panel-desc">Find messages older than a chosen age window.</p>
              <div className="sm-toolbar">
                <select
                  className="sm-select"
                  value={olderThan}
                  onChange={(e) => setOlderThan(e.target.value)}
                >
                  <option value="6m">6 months</option>
                  <option value="1y">1 year</option>
                  <option value="2y">2 years</option>
                  <option value="5y">5 years</option>
                </select>
                <select
                  className="sm-select"
                  value={folder}
                  onChange={(e) => setFolder(e.target.value)}
                >
                  <option value="">All folders</option>
                  <option value="inbox">Inbox</option>
                  <option value="sent">Sent</option>
                  <option value="spam">Spam</option>
                </select>
                <input
                  className="sm-input"
                  placeholder="Search sender or subject…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button type="button" className="sm-btn" onClick={loadModuleData}>
                  Apply
                </button>
                {selectedIds.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="sm-btn"
                      onClick={() => runCleanup('archive_old', selectedIds)}
                    >
                      Archive
                    </button>
                    <button
                      type="button"
                      className="sm-btn sm-btn-danger"
                      onClick={() => runCleanup('move_to_trash', selectedIds)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
              {moduleLoading ? <div className="sm-skeleton" /> : renderMailTable(oldMails.items)}
            </section>
          )}

          {module === 'newsletters' && (
            <section className="sm-panel">
              <h2>Newsletter cleanup</h2>
              <p className="sm-panel-desc">
                Detected via List-Unsubscribe patterns and common newsletter senders.
              </p>
              <div className="sm-toolbar">
                <input
                  className="sm-input"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button type="button" className="sm-btn" onClick={loadModuleData}>
                  Apply
                </button>
                {selectedIds.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="sm-btn"
                      onClick={() => runCleanup('archive_old', selectedIds)}
                    >
                      Archive all selected
                    </button>
                    <button
                      type="button"
                      className="sm-btn sm-btn-danger"
                      onClick={() => runCleanup('move_to_trash', selectedIds)}
                    >
                      Delete all selected
                    </button>
                  </>
                )}
              </div>
              {moduleLoading ? <div className="sm-skeleton" /> : renderMailTable(newsletters.items)}
            </section>
          )}

          {module === 'trash' && (
            <section className="sm-panel">
              <h2>Empty Trash</h2>
              <p className="sm-panel-desc">Permanently remove trash stored in the app database.</p>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : (
                <>
                  <div className="sm-kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                    <div className="sm-kpi-card">
                      <span className="sm-kpi-label">Trash size</span>
                      <span className="sm-kpi-value">{formatBytes(trashInfo?.size)}</span>
                    </div>
                    <div className="sm-kpi-card">
                      <span className="sm-kpi-label">Email count</span>
                      <span className="sm-kpi-value">{trashInfo?.count || 0}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sm-btn sm-btn-danger"
                    onClick={() => runCleanup('empty_trash_mongo')}
                  >
                    Permanent delete (app trash)
                  </button>
                </>
              )}
            </section>
          )}

          {module === 'spam' && (
            <section className="sm-panel">
              <h2>Empty Spam</h2>
              <p className="sm-panel-desc">Moves spam to Trash first for safety.</p>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : (
                <>
                  <div className="sm-kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                    <div className="sm-kpi-card">
                      <span className="sm-kpi-label">Spam size</span>
                      <span className="sm-kpi-value">{formatBytes(spamInfo?.size)}</span>
                    </div>
                    <div className="sm-kpi-card">
                      <span className="sm-kpi-label">Email count</span>
                      <span className="sm-kpi-value">{spamInfo?.count || 0}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sm-btn sm-btn-danger"
                    onClick={() => runCleanup('empty_spam_mongo')}
                  >
                    Move spam to Trash
                  </button>
                </>
              )}
            </section>
          )}

          {module === 'labels' && (
            <section className="sm-panel">
              <h2>Unused labels</h2>
              <p className="sm-panel-desc">Labels containing zero emails. Manage them in Labels or delete unused ones.</p>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : !unusedLabels.length ? (
                <div className="sm-empty"><h3>No unused labels</h3></div>
              ) : (
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Count</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unusedLabels.map((l) => (
                      <tr key={l._id}>
                        <td className="subject">{l.name}</td>
                        <td>0</td>
                        <td>
                          <button
                            type="button"
                            className="sm-btn sm-btn-sm"
                            onClick={() => navigate('/inbox')}
                          >
                            Open inbox
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {module === 'folders' && (
            <section className="sm-panel">
              <h2>Empty folders</h2>
              <p className="sm-panel-desc">Folders with no emails from the last storage scan.</p>
              {moduleLoading ? (
                <div className="sm-skeleton" />
              ) : !emptyFolders.length ? (
                <div className="sm-empty"><h3>No empty folders detected</h3></div>
              ) : (
                <ul>
                  {emptyFolders.map((f) => (
                    <li key={f.folder} style={{ textTransform: 'capitalize', marginBottom: 6 }}>
                      {f.folder}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {module === 'analytics' && (
            <>
              <div className="sm-charts">
                <section className="sm-panel">
                  <h2>Largest senders</h2>
                  <div className="sm-bar-list">
                    {(reports?.overview?.largestSenders || overview?.largestSenders || []).slice(0, 8).map((s) => {
                      const max = (overview?.largestSenders?.[0]?.size) || 1;
                      return (
                        <div className="sm-bar-row" key={s.email}>
                          <span className="label" title={s.email}>{s.email}</span>
                          <div className="sm-bar-track">
                            <i style={{ width: `${Math.round(((s.size || 0) / max) * 100)}%` }} />
                          </div>
                          <span className="val">{formatBytes(s.size)}</span>
                        </div>
                      );
                    })}
                    {!(overview?.largestSenders || []).length && (
                      <div className="sm-empty"><p>Scan to populate analytics.</p></div>
                    )}
                  </div>
                </section>
                <section className="sm-panel">
                  <h2>Oldest mail buckets</h2>
                  <div className="sm-bar-list">
                    {[
                      ['6+ months', overview?.oldMailCounts?.m6],
                      ['1+ year', overview?.oldMailCounts?.y1],
                      ['2+ years', overview?.oldMailCounts?.y2],
                      ['5+ years', overview?.oldMailCounts?.y5],
                    ].map(([label, count]) => (
                      <div className="sm-bar-row" key={label}>
                        <span className="label">{label}</span>
                        <div className="sm-bar-track">
                          <i style={{ width: `${Math.min(100, (count || 0) / 2)}%` }} />
                        </div>
                        <span className="val">{count || 0}</span>
                      </div>
                    ))}
                  </div>
                  <p className="sm-panel-desc" style={{ marginTop: 12 }}>
                    Newsletters: {overview?.newsletterCount || 0} · Duplicates:{' '}
                    {overview?.duplicateEmails || 0} · Recoverable:{' '}
                    {formatBytes(overview?.recoverableEstimate)}
                  </p>
                </section>
              </div>
              <section className="sm-panel">
                <h2>Cleanup history</h2>
                {!reports?.history?.length ? (
                  <div className="sm-empty"><p>No cleanup actions yet.</p></div>
                ) : (
                  <table className="sm-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Action</th>
                        <th>Affected</th>
                        <th>Freed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.history.map((h) => (
                        <tr key={h._id}>
                          <td className="meta">{formatDate(h.createdAt)}</td>
                          <td>{h.action}</td>
                          <td>{h.affectedCount}</td>
                          <td>{formatBytes(h.freedBytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}

          {module === 'rules' && (
            <section className="sm-panel">
              <h2>Auto cleanup rules</h2>
              <p className="sm-panel-desc">
                Example: delete spam older than 30 days, archive mail older than 2 years, empty trash weekly.
                Rules notify before cleanup when enabled.
              </p>
              <div className="sm-toolbar">
                <button type="button" className="sm-btn sm-btn-primary" onClick={addRule}>
                  Add sample rule
                </button>
              </div>
              {!rules.length ? (
                <div className="sm-empty"><h3>No rules yet</h3></div>
              ) : (
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Enabled</th>
                      <th>Notify</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => (
                      <tr key={r._id}>
                        <td className="subject">{r.name}</td>
                        <td className="meta">{r.type}</td>
                        <td>{r.enabled ? 'Yes' : 'No'}</td>
                        <td>{r.notifyBefore ? 'Yes' : 'No'}</td>
                        <td>
                          <button
                            type="button"
                            className="sm-btn sm-btn-sm sm-btn-danger"
                            onClick={() => deleteRule(r._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {module === 'settings' && settings && (
            <section className="sm-panel">
              <h2>Storage settings</h2>
              <div className="sm-settings-grid">
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.enableAutoCleanup)}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, enableAutoCleanup: e.target.checked }))
                    }
                  />
                  Enable auto cleanup
                </label>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.automaticScan)}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, automaticScan: e.target.checked }))
                    }
                  />
                  Automatic scan
                </label>
                <label>
                  Storage warning (%)
                  <input
                    className="sm-input"
                    type="number"
                    min="50"
                    max="100"
                    value={settings.storageWarningPercent}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        storageWarningPercent: Number(e.target.value),
                      }))
                    }
                  />
                </label>
                <label>
                  Cleanup reminder (days)
                  <input
                    className="sm-input"
                    type="number"
                    min="1"
                    value={settings.cleanupReminderDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        cleanupReminderDays: Number(e.target.value),
                      }))
                    }
                  />
                </label>
                <label>
                  Attachment threshold (MB)
                  <input
                    className="sm-input"
                    type="number"
                    min="1"
                    value={settings.attachmentThresholdMb}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        attachmentThresholdMb: Number(e.target.value),
                      }))
                    }
                  />
                </label>
                <label>
                  Duplicate sensitivity
                  <select
                    className="sm-select"
                    value={settings.duplicateSensitivity}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, duplicateSensitivity: e.target.value }))
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
              </div>
              <div className="sm-toolbar" style={{ marginTop: 16 }}>
                <button type="button" className="sm-btn sm-btn-primary" onClick={saveSettings}>
                  Save settings
                </button>
              </div>
            </section>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={Boolean(confirm?.open)}
        message={confirm?.message || ''}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
      <AlertModal
        isOpen={alert.open}
        message={alert.message}
        onClose={() => setAlert({ open: false, message: '' })}
      />
    </div>
  );
}
